import { ITwoFACodeResult } from '../../customTypings/auth/TwoFACodeResult';
import { useAtom } from "@dbeining/react-atom";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Formik } from "formik";
import { useEffect, useState, useRef } from "react";
import { Alert, Form, Image, Spinner } from "react-bootstrap";
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from "react-router-dom";
import VerificationInput from "react-verification-input";
import { updateUserStateAtom, userAtom } from "../../atoms";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { IUser } from "../../customTypings/user/User";
import { clone } from "../../utils";
import { authenticationService } from "../../services";

const TwoFASetupPage = () => {
    let navigate = useNavigate();
    let location = useLocation();
    const [qRCode, setQRCode] = useState<ITwoFACodeResult | undefined>()
    const [twoFactorQrCode, setTwoFactorQrCode] = useState('')
    const [twoFactorSetupComplete, setTwoFactorSetupComplete] = useState(false)
    const currentUserAtom = useAtom(userAtom)
    const [loading, setLoading] = useState(true)
    const [statusMessage, setStatusMessage] = useState("")
    const verificationInputRef = useRef<any>(null)
    const [countdown, setCountdown] = useState(0)
    const [isGenerating, setIsGenerating] = useState(false)

    const originalDestination = (location.state as any)?.from?.pathname || "/";

    useEffect(() => {
        generateInitialTwoFAQRCode()
        if(currentUserAtom === undefined ||
            currentUserAtom.advisories === undefined ||
            (currentUserAtom.advisories !== undefined && currentUserAtom.advisories?.twoFaSetupRequired === undefined))
        {
            navigate("/", { state: { message: "Ok" } });
        }
        setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (qRCode && verificationInputRef.current) {
            setTimeout(() => {
                if (verificationInputRef.current) {
                    verificationInputRef.current.focus();
                }
            }, 100);
        }
    }, [qRCode])

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (countdown > 0) {
            interval = setInterval(() => {
                setCountdown(prev => prev - 1);
            }, 1000);
        } else if (countdown === 0 && isGenerating) {
            setIsGenerating(false);
        }
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [countdown, isGenerating]);

    function generateInitialTwoFAQRCode() {
        setStatusMessage("");
        authenticationService.generateTwoFAQRCode().then((response: ITwoFACodeResult) => {
            setQRCode(response)
        },
        error => {
            if(error.status === 400){
               setStatusMessage('Account enable failed.');
            } else {
               setStatusMessage(error);
            }
            console.log("2FA", error)
        });
    }

    function generateTwoFAQRCode() {
        setStatusMessage("");
        setIsGenerating(true);
        setCountdown(30);
        authenticationService.generateTwoFAQRCode().then((response: ITwoFACodeResult) => {
            setQRCode(response)
        },
        error => {
            if(error.status === 400){
               setStatusMessage('Account enable failed.');
            } else {
               setStatusMessage(error);
            }
            console.log("2FA", error)
        });
    }

    if (loading) {
        return (
            <div className="login-page login-page--2fa-setup p-3 d-flex align-items-center justify-content-center min-vh-100">
                <div className="text-center">
                    <Image
                        src="/images/froom-waste-tracker-logo.svg"
                        className="login-brand-logo mb-3 froom-brand"
                        alt="Froom Waste Tracker"
                    />
                    <Spinner animation="border" />
                </div>
            </div>
        )
    }

    return (
        <div className="login-page login-page--2fa-setup p-3 position-relative">
            <Helmet>
                <title>Two-Factor Authentication Setup - Froom Tracker</title>
            </Helmet>

            <div className="position-absolute top-0 start-0 m-3 z-1">
                <button
                    type="button"
                    className="btn-secondary-custom"
                    onClick={() => {
                        updateUserStateAtom(undefined);
                        navigate('/signin');
                    }}
                >
                    ← Back to Login
                </button>
            </div>

            <div className="login-brand-section">
                <Image
                    src="/images/froom-waste-tracker-logo.svg"
                    className="login-brand-logo h-20rem froom-brand"
                    alt="Froom Waste Tracker"
                />
                <Image
                    src="/images/waste-tracking-system-strapline.png"
                    alt="Froom waste tracking system"
                    className="login-strapline-image"
                />
            </div>

            <div className="login-form-container login-form-container--wide">
                <h1 className="password-title">Two-Factor Authentication Setup</h1>
                {!twoFactorSetupComplete && (
                    <p>
                        Please use an authenticator app (such as Google Authenticator or Microsoft Authenticator) to scan this QR code.
                    </p>
                )}

                {statusMessage && (
                    <Alert variant="danger" className="mb-3">{statusMessage}</Alert>
                )}

                <div className="tfa-bottom-sections">
                    <div className="tfa-right-content">
                        {!twoFactorSetupComplete && qRCode !== undefined && (
                            <>
                                <div className="text-center mb-4">
                                    <Image fluid src={`data:${qRCode.qrCodeContentType};base64,${qRCode.qrCodeContent}`} alt="QR code for two-factor authentication" height={qRCode.qrCodeHeight} width={qRCode.qrCodeWidth} />
                                </div>
                                <div className="text-center">
                                    <p className="mb-2">Or enter this code manually into your authenticator app of choice:</p>
                                    <div className="tfa-code">{qRCode.secretKey}</div>
                                </div>
                                <div className="text-center mt-4">
                                    <button
                                        type="button"
                                        className="btn-danger-custom mt-2 me-2"
                                        onClick={() => generateTwoFAQRCode()}
                                        disabled={isGenerating}
                                    >
                                        {isGenerating ? (
                                            <>{countdown} Generate new Key/QR Code</>
                                        ) : (
                                            <><FontAwesomeIcon icon={faArrowsRotate}/> Generate new Key/QR Code</>
                                        )}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="tfa-verify-content">
                        {!twoFactorSetupComplete ? (
                            qRCode !== undefined ? (
                                <Formik
                                    initialValues={{}}
                                    onSubmit={async (_, { setStatus, setSubmitting }) => {
                                        setStatus();
                                        await authenticationService.updateTwoFASecretKey(qRCode.secretKey, twoFactorQrCode).then(() => {
                                            setTwoFactorSetupComplete(true)
                                            const copy = clone<IUser>(currentUserAtom as IUser);
                                            copy.advisories = undefined;
                                            updateUserStateAtom(copy)
                                            setSubmitting(false)
                                            navigate(originalDestination, { state: { message: "Ok" } })
                                        },
                                        error => {
                                            setSubmitting(false);
                                            if(error.status === 400){
                                                setStatus(error.message);
                                            } else {
                                                setStatus(error);
                                            }
                                        });
                                    }}
                                >
                                    {({ status, isSubmitting, handleSubmit }) => (
                                        <Form noValidate onSubmit={handleSubmit}>
                                            <div className="tfa-otp-container">
                                                <div className="label">Validation Code</div>
                                                <div className="tfa-otp-inputs">
                                                    <VerificationInput
                                                        ref={verificationInputRef}
                                                        value={twoFactorQrCode}
                                                        length={6}
                                                        validChars={'0-9'}
                                                        placeholder={''}
                                                        onChange={value => setTwoFactorQrCode(value)}
                                                        classNames={{
                                                            container: "tfa-otp-input"
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="text-center mt-4">
                                                <button
                                                    className="btn-primary-custom"
                                                    disabled={isSubmitting || twoFactorQrCode.length !== 6}
                                                    type="submit"
                                                >
                                                    {isSubmitting
                                                        ?  <LoadingSpinner text="Verifying..."/>
                                                        : "Verify"}
                                                </button>
                                            </div>
                                            {status &&
                                                <div className={'alert alert-danger mt-3'}>{status}</div>
                                            }
                                        </Form>
                                    )}
                                </Formik>
                            ) : null
                        ) : (
                            <div className="text-center">
                                <p className="mb-3">Two-Factor setup complete</p>
                                <button
                                    type="button"
                                    className="btn-secondary-custom"
                                    onClick={() => navigate(originalDestination, { state: { message: "Ok" } })}
                                >
                                    Continue
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export { TwoFASetupPage };
